/* global __BUILD_ID__ */
import { safeStorageGetTolerant, safeStorageSet, safeStorageRemove } from '../storage/safe-storage.js';

const STORAGE_KEY = 'adminsr_design';

/**
 * Absolute href for a design's theme CSS, carrying the build-version cache-bust
 * suffix (`?v=<build id>`). `__BUILD_ID__` is injected at build time (esbuild
 * define) and mirrors the `?v=` the rendered index.html puts on the baseline
 * <link>, so a runtime theme swap requests the same versioned URL. Single owner
 * of the CSS href shape — used by both the boot reconcile and `set()`.
 * @param {string} design
 * @returns {string}
 */
function cssHref(design) {
  return new URL(`./css/${design}.css?v=${__BUILD_ID__}`, document.baseURI).href;
}

/**
 * Runtime design (theme file) switching manager.
 * Swaps the <link id="adminsr-design-css"> href to load a different theme's CSS.
 *
 * @param {{ availableDesigns: string[], defaultDesign?: string }} opts
 *   `defaultDesign` is the baseline design used when nothing is stored; it is
 *   never persisted (only overrides are). Defaults to 'default'.
 * @returns {{
 *   get(): string,
 *   getAvailable(): string[],
 *   set(design: string): void,
 *   subscribe(fn: (design: string) => void): () => void,
 *   destroy(): void,
 * }}
 */
export function createDesignManager({ availableDesigns, defaultDesign = 'default' }) {
  // Validate the baseline against the registered designs, so a misconfigured
  // caller can never make `current` point at unregistered (404) CSS. Mirrors
  // the `availableDesigns.includes(stored)` guard used for the stored value.
  if (!availableDesigns.includes(defaultDesign)) {
    defaultDesign = availableDesigns[0] ?? 'default';
  }

  /** @type {Array<(design: string) => void>} */
  let subscribers = [];
  /** @type {HTMLLinkElement | null} */
  let pendingPreload = null;

  // Init-sync: read stored value, validate, reconcile DOM.
  // Visual truth is source of truth: if the CSS link element is missing,
  // we cannot confirm or apply any design, so we stay on the baseline
  // (defaultDesign) and clean up stale storage. This aligns init with
  // set()'s no-op rule.
  const stored = safeStorageGetTolerant(STORAGE_KEY, null);
  /** @type {string} */
  let current;
  const link = document.getElementById('adminsr-design-css');

  if (!link) {
    // No CSS link — cannot verify visual state; fall back to baseline
    current = defaultDesign;
    if (stored !== null) {
      safeStorageRemove(STORAGE_KEY);
    }
  } else if (stored && availableDesigns.includes(stored)) {
    current = stored;
    // Reconcile DOM link to match resolved design
    const expectedHref = cssHref(current);
    if (/** @type {HTMLLinkElement} */ (link).href !== expectedHref) {
      /** @type {HTMLLinkElement} */ (link).href = expectedHref;
    }
  } else {
    current = defaultDesign;
    if (stored !== null) {
      safeStorageRemove(STORAGE_KEY);
    }
    // Revert DOM link to baseline if needed
    const expectedHref = cssHref(defaultDesign);
    if (/** @type {HTMLLinkElement} */ (link).href !== expectedHref) {
      /** @type {HTMLLinkElement} */ (link).href = expectedHref;
    }
  }

  function notify() {
    for (const fn of subscribers) fn(current);
  }

  /** @param {string} design */
  function persist(design) {
    // Baseline design is the implicit no-storage state — only overrides persist.
    if (design === defaultDesign) {
      safeStorageRemove(STORAGE_KEY);
    } else {
      safeStorageSet(STORAGE_KEY, design);
    }
  }

  return {
    get() {
      return current;
    },

    getAvailable() {
      return availableDesigns;
    },

    set(design) {
      if (!availableDesigns.includes(design)) return;
      if (design === current) return;

      // Cancel any in-flight swap
      if (pendingPreload) {
        pendingPreload.onload = null;
        pendingPreload.onerror = null;
        pendingPreload.remove();
        pendingPreload = null;
      }

      const linkEl = /** @type {HTMLLinkElement | null} */ (document.getElementById('adminsr-design-css'));
      if (!linkEl) return; // no CSS link — cannot visually swap, stay unchanged

      const newHref = cssHref(design);

      // Load new stylesheet WITHOUT applying it (media="not all" prevents cascade)
      const preload = /** @type {HTMLLinkElement} */ (document.createElement('link'));
      preload.rel = 'stylesheet';
      preload.media = 'not all';
      preload.href = newHref;
      pendingPreload = preload;

      preload.onload = () => {
        if (pendingPreload !== preload) return; // superseded by newer swap
        pendingPreload = null;
        linkEl.href = newHref;
        preload.onload = null;
        preload.onerror = null;
        preload.remove();
        current = design;
        persist(design);
        notify();
      };

      preload.onerror = () => {
        if (pendingPreload !== preload) return;
        pendingPreload = null;
        preload.onload = null;
        preload.onerror = null;
        preload.remove();
        // CSS failed to load — keep current design unchanged
      };

      document.head.appendChild(preload);
    },

    subscribe(fn) {
      subscribers.push(fn);
      return () => {
        subscribers = subscribers.filter((s) => s !== fn);
      };
    },

    destroy() {
      if (pendingPreload) {
        pendingPreload.onload = null;
        pendingPreload.onerror = null;
        pendingPreload.remove();
        pendingPreload = null;
      }
      subscribers = [];
    },
  };
}
